// File: oneportal-shell/src/pages/ManageLocations/manageLocationsService.ts

import { createRisClient } from "@/api/risClient";
import { getApiUrl } from "@/api/apiConfig";
import { apiService } from "@/services/apiService";
import type {
  UpdateConsumerDeviceLocationModel,
  CountryCode,
} from "@resideo/web-integration-services-api-client";

// ─── Domain types ──────────────────────────────────────────────────────────

export type LocationHealthStatus = "Good" | "Fair" | "Poor";

export interface LocationAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvinceRegionCode?: string;
  zipPostalCode?: string;
  countryCode?: string;
}

export interface LocationGeoCoordinate {
  latitude?: number;
  longitude?: number;
  elevation?: number;
}

export interface ManagedLocation {
  id: string;
  name: string;
  consumerAccountId: string;
  address: LocationAddress;
  geoCoordinate?: LocationGeoCoordinate;
  consumerDeviceIds: string[];
  healthStatus: LocationHealthStatus;
  totalDevices: number;
  healthyDevices: number;
  offlineDevices: number;
  alertCount: number;
}

export interface UpdateLocationInput {
  consumerAccountId: string;
  locationId: string;
  name: string;
  address: LocationAddress;
  geoCoordinate?: LocationGeoCoordinate;
  consumerDeviceIds?: string[];
}

const getAccessToken = (): string | null =>
  localStorage.getItem("oneportal_auth_token") ??
  localStorage.getItem("authToken");

const SELECTED_CONTEXT_KEY = "oneportal_selected_context";

export async function fetchLocations(): Promise<ManagedLocation[]> {
  await apiService.getAuthToken();
  const risClient = createRisClient(getApiUrl("risPublicApi"), getAccessToken);
  const accountType = localStorage.getItem(SELECTED_CONTEXT_KEY) ?? undefined;

  const response = await risClient.myAccounts.myAccountsGetMyConsumerAccountV2(accountType);
  const consumerUsers = response.data?.consumerUsers ?? [];

  const locationsById = new Map<string, ManagedLocation>();
  const accountIds = new Set<string>();

  for (const consumerUser of consumerUsers) {
    const account = consumerUser.consumerAccount;
    const accountId = account?.id ?? "";
    if (!accountId) continue;
    accountIds.add(accountId);

    for (const loc of account?.locations ?? []) {
      if (!loc.id || locationsById.has(loc.id)) continue;
      locationsById.set(loc.id, {
        id: loc.id,
        name: loc.name ?? "Unknown Location",
        consumerAccountId: accountId,
        address: {
          addressLine1: loc.address?.addressLine1 ?? "",
          addressLine2: loc.address?.addressLine2 ?? "",
          city: loc.address?.city ?? "",
          stateProvinceRegionCode: loc.address?.stateProvinceRegionCode ?? "",
          zipPostalCode: loc.address?.zipPostalCode ?? "",
          countryCode: (loc.address?.countryCode as string | undefined) ?? "US",
        },
        geoCoordinate: loc.geoCoordinate
          ? {
              latitude: loc.geoCoordinate.latitude,
              longitude: loc.geoCoordinate.longitude,
              elevation: loc.geoCoordinate.elevation ?? 0,
            }
          : undefined,
        consumerDeviceIds: (loc.consumerDevices ?? [])
          .map((d) => d.id)
          .filter((id): id is string => Boolean(id)),
        healthStatus: "Good",
        totalDevices: 0,
        healthyDevices: 0,
        offlineDevices: 0,
        alertCount: 0,
      });
    }
  }

  await Promise.allSettled(
    Array.from(accountIds).map(async (accountId) => {
      const healthResponse = await risClient.health.myAccountsGetLocationsHealth(accountId);
      for (const h of healthResponse.data ?? []) {
        const target = h.locationId ? locationsById.get(h.locationId) : undefined;
        if (!target) continue;
        target.healthStatus = (h.overallHealthStatus as LocationHealthStatus) ?? "Good";
        target.totalDevices = h.totalDevices ?? 0;
        target.healthyDevices = h.healthyDevices ?? 0;
        target.offlineDevices = h.totalOfflineDevices ?? 0;
        target.alertCount = h.totalAlerts ?? 0;
      }
    }),
  );

  return Array.from(locationsById.values());
}

export async function updateLocation(input: UpdateLocationInput): Promise<void> {
  await apiService.getAuthToken();
  const risClient = createRisClient(getApiUrl("risPublicApi"), getAccessToken);

  const body: UpdateConsumerDeviceLocationModel = {
    name: input.name,
    address: {
      addressLine1: input.address.addressLine1 ?? "",
      addressLine2: input.address.addressLine2 ?? "",
      city: input.address.city ?? "",
      stateProvinceRegionCode: input.address.stateProvinceRegionCode ?? "",
      zipPostalCode: input.address.zipPostalCode ?? "",
      countryCode: (input.address.countryCode ?? "US") as CountryCode,
      mailingAddressName: null,
      contactName: null,
    },
    geoCoordinate: input.geoCoordinate ?? undefined,
    consumerDeviceIds: input.consumerDeviceIds ?? [],
  };

  await risClient.myAccounts.myAccountsUpdateConsumerDeviceLocation(
    input.consumerAccountId,
    input.locationId,
    body,
  );
}
